/**
 * Grid library templates — deployable strategy / agent config stubs (Advanced+).
 *
 * WORK-PLAN C1/C3: multi-exchange grid library under the agent model.
 * Templates are strategy configs you deploy as your agents — not signals,
 * not copy-trading feeds, not stranger leaderboards.
 *
 * Honesty rules (PRODUCT / marketing non-goals):
 * - No invented returns, APY, win rates, or backtest P&L.
 * - Exchange lists are placeholders for connectors you will attach.
 * - Defaults are structural config hints only, not performance promises.
 */

export type GridTemplateId =
  | 'symmetric-range'
  | 'spot-accumulate'
  | 'tight-micro'
  | 'wide-swing'
  | 'neutral-inventory'
  | 'multi-exchange-range'

/** Structural defaults only — never performance metrics. */
export interface GridTemplateDefaults {
  /** Number of grid price levels (config). */
  gridLevels: number
  /** Human-readable range style for operators. */
  rangeStyle: string
  /** How fills rebalance inventory (config label). */
  rebalance: string
}

export interface GridTemplate {
  id: GridTemplateId
  name: string
  /** Plain operator description — what the strategy does, not what it returns. */
  description: string
  /**
   * Exchange connector placeholders. Real venue wiring is configured at deploy.
   * Do not treat these as live multi-exchange counts for marketing.
   */
  exchanges: readonly string[]
  kind: 'grid'
  defaults: GridTemplateDefaults
  /** Optional operator note (risk / use-case), never a performance claim. */
  notes?: string
}

/**
 * Honest template catalog (3–6 stubs). Deploy creates *your* agent/strategy
 * config — not a signal subscription.
 */
export const GRID_TEMPLATES: readonly GridTemplate[] = [
  {
    id: 'symmetric-range',
    name: 'Symmetric range grid',
    description:
      'Places buy and sell ladder orders evenly across a price band you set. Classic range grid for pairs expected to oscillate inside bounds.',
    exchanges: ['Exchange A (placeholder)', 'Exchange B (placeholder)'],
    kind: 'grid',
    defaults: {
      gridLevels: 12,
      rangeStyle: 'Equal spacing in band',
      rebalance: 'Symmetric after fill',
    },
    notes: 'Requires a defined upper and lower bound; not a trend-follow strategy.',
  },
  {
    id: 'spot-accumulate',
    name: 'Spot accumulate grid',
    description:
      'Biases the ladder toward buying more size on dips within the band so the agent can build inventory over time. Still a grid config — not a DCA signal feed.',
    exchanges: ['Exchange A (placeholder)'],
    kind: 'grid',
    defaults: {
      gridLevels: 10,
      rangeStyle: 'Asymmetric buy weight in band',
      rebalance: 'Favor long inventory',
    },
    notes: 'Inventory will grow if price drifts down through the band; size risk carefully.',
  },
  {
    id: 'tight-micro',
    name: 'Tight micro-grid',
    description:
      'Dense level spacing for lower-volatility pairs or narrow operator-defined bands. More orders per unit of range; higher fill churn potential.',
    exchanges: ['Exchange A (placeholder)', 'Exchange B (placeholder)'],
    kind: 'grid',
    defaults: {
      gridLevels: 20,
      rangeStyle: 'Narrow band, dense levels',
      rebalance: 'Symmetric after fill',
    },
    notes: 'Fee-sensitive: dense grids need exchange fee awareness at deploy.',
  },
  {
    id: 'wide-swing',
    name: 'Wide swing grid',
    description:
      'Wider spacing across a larger band for higher-volatility pairs. Fewer levels, larger step size between orders.',
    exchanges: ['Exchange A (placeholder)'],
    kind: 'grid',
    defaults: {
      gridLevels: 8,
      rangeStyle: 'Wide band, coarse levels',
      rebalance: 'Symmetric after fill',
    },
    notes: 'Expect longer idle stretches between fills when the band is wide.',
  },
  {
    id: 'neutral-inventory',
    name: 'Neutral inventory grid',
    description:
      'Targets roughly flat inventory by rebalancing both sides of the book after fills. Operator sets band and size caps; agent config enforces inventory bounds.',
    exchanges: ['Exchange A (placeholder)', 'Exchange B (placeholder)'],
    kind: 'grid',
    defaults: {
      gridLevels: 14,
      rangeStyle: 'Equal spacing with inventory caps',
      rebalance: 'Inventory-neutral target',
    },
    notes: 'Caps are config, not guarantees against gap risk outside the band.',
  },
  {
    id: 'multi-exchange-range',
    name: 'Multi-exchange range grid',
    description:
      'Same range-grid structure with placeholders for more than one connected exchange. Deploy attaches only venues you have linked — not a public multi-venue promise.',
    exchanges: [
      'Exchange A (placeholder)',
      'Exchange B (placeholder)',
      'Exchange C (placeholder)',
    ],
    kind: 'grid',
    defaults: {
      gridLevels: 12,
      rangeStyle: 'Equal spacing per linked venue',
      rebalance: 'Per-venue symmetric',
    },
    notes: 'Advanced plan library surface; live multi-exchange needs connected brokers.',
  },
] as const

export function getGridTemplate(id: GridTemplateId): GridTemplate | undefined {
  return GRID_TEMPLATES.find((t) => t.id === id)
}

export function listGridTemplates(): readonly GridTemplate[] {
  return GRID_TEMPLATES
}
